import { DateTime, Duration, Interval, Settings } from 'luxon';
import React, { HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { TimePicker, TimePickerDefaults } from './time-picker/time-picker';
import './date-picker.scss';
import classNames from 'classnames';
import '@fortawesome/fontawesome-free/css/all.css';
import usePrevious from '../../hooks/use-previous';
import { Calendar } from './calendar/calendar';
import _ from 'lodash';
import { parseIsoTime } from '../../util/date-time-util';
import { useOnClickOutside } from '../../util/use-on-click-outside';
import Icon from '../icons/icon';
import { Typography } from '../typography/typography';
import { Portal } from '../portal/portal';
import { usePopper } from 'react-popper';
import { Button } from '../form/button';
import { FieldLabel, FieldLabelProps } from '../field/field';

type DateTimePickerProps = Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'ref' | 'clasName' | 'label'> &
  FieldLabelProps & {
    name?: string;
    value?: DateTime;
    onChange?: (d?: DateTime) => void;
    disabledDaysOfWeek?: number[];
    disabledIntervals?: Interval[];
    highlightedIntervals?: { intervals: Interval; color: string }[];
    step?: Duration;
    timezone?: string;
    minTime?: string;
    maxTime?: string;
    restrictToTimes?: string[];
    customDisplay?: ReactNode;
    customErrorDisplay?: ReactNode;
    usePortal?: boolean;
    clearable?: boolean;
  };

export const DateTimePicker = ({
  name,
  value,
  onChange,
  disabledDaysOfWeek,
  disabledIntervals,
  highlightedIntervals,
  step,
  timezone = Settings.defaultLocale,
  minTime = TimePickerDefaults.min,
  maxTime = TimePickerDefaults.max,
  restrictToTimes = TimePickerDefaults.restrictToTimes,
  customDisplay,
  customErrorDisplay,
  usePortal = false,
  clearable = false,
  label,
  labelType,
  labelWeight,
  isRequired,
  ...props
}: DateTimePickerProps) => {
  const [inputDateStr, setInputDateStr] = useState<string>();
  const [error, setError] = useState<string | boolean>();
  const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState<boolean>(false);
  const [didUpdateDateStr, setDidUpdateDateStr] = useState<boolean>(false);
  const [pickerView, setPickerView] = useState<'date' | 'time'>('date');
  const divRef = useRef<HTMLDivElement>(null);
  const portalContent = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const [portalWrapper, setPortalWrapper] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(portalWrapper, portalContainer, { placement: 'bottom-start' });
  const previousValue = usePrevious(value);
  const previousInputDateStr = usePrevious(inputDateStr);

  useEffect(() => {
    if (value) {
      if (value.isValid) {
        if (
          !previousValue ||
          value.diff(previousValue).milliseconds !== 0 ||
          value.zoneName !== previousValue.zoneName
        ) {
          const dateString = value?.toFormat('D t') || '';
          setInputDateStr(dateString);
          setDidUpdateDateStr(false);
        }
        const isDisabledDayOfWeek = disabledDaysOfWeek?.find((dow) => value.weekday === dow);
        const isInDisabledInterval = !!disabledIntervals?.find((i) => i.contains(value));
        const isBeforeMinTime = value.set(parseIsoTime(minTime)).diff(value).milliseconds > 0;
        const isAfterMaxTime = value.set(parseIsoTime(maxTime)).diff(value).milliseconds < 0;
        const isNotARestrictedTime =
          restrictToTimes?.length &&
          !restrictToTimes.find((t) => value.set(parseIsoTime(t)).diff(value).milliseconds === 0);
        if (isDisabledDayOfWeek || isInDisabledInterval || isBeforeMinTime || isAfterMaxTime || isNotARestrictedTime) {
          setError('The selected time is not available');
        } else {
          setError(undefined);
        }
      }
    } else {
      setInputDateStr('');
      setDidUpdateDateStr(false);
      setError(undefined);
    }
  }, [value, previousValue, disabledIntervals]);

  function tryParseDateTime(inputDateStr: string): DateTime {
    const localesToTry = [Intl.DateTimeFormat().resolvedOptions().locale, undefined, 'en-US'];
    let dateTime = undefined;
    for (let i = 0; i < localesToTry.length; i++) {
      dateTime = DateTime.fromFormat(inputDateStr, 'D t', { zone: timezone, locale: localesToTry[i] });
      if (dateTime.isValid) {
        return dateTime;
      } else {
        const explain = DateTime.fromFormatExplain(inputDateStr, 'D t', { zone: timezone, locale: localesToTry[i] });
        const fmt = explain.tokens
          .map((t) => t.val)
          .join('')
          .replace(/\s/g, ' ');
        dateTime = DateTime.fromFormat(inputDateStr, fmt, { zone: timezone, locale: localesToTry[i] });
        if (dateTime.isValid) {
          return dateTime;
        }
      }
    }

    if (dateTime?.isValid) {
      return dateTime;
    } else {
      throw Error('Failed to parse date time');
    }
  }

  const handleInputDateStrUpdate = useRef(
    _.debounce((inputDateStr: string | undefined, didUpdateDateStr: boolean, clearable: boolean) => {
      if (didUpdateDateStr) {
        if (inputDateStr) {
          try {
            const dateTime = tryParseDateTime(inputDateStr);
            if (dateTime?.invalidReason) {
              setError('Not a valid date or time');
            } else {
              setError(undefined);
            }
            onChange && onChange(dateTime);
            setDidUpdateDateStr(false);
          } catch (e) {
            setError('Not a valid date or time');
          }
        } else if (clearable) {
          setError(undefined);
          onChange && onChange(undefined);
          setDidUpdateDateStr(false);
        }
      }
    }, 300),
  ).current;

  useEffect(() => {
    if (previousInputDateStr !== inputDateStr) {
      handleInputDateStrUpdate(inputDateStr, didUpdateDateStr, clearable);
    }
  }, [inputDateStr, previousInputDateStr, didUpdateDateStr, onChange, clearable]);

  useOnClickOutside(usePortal ? portalContent : divRef, () => {
    setIsDateTimePickerOpen(false);
    setPickerView('date');
  });

  useEffect(() => {
    onChange && onChange(value?.setZone(timezone));
  }, [timezone]);

  const DateTimePickerDropdown = ({ showClearButton = false }: { showClearButton?: boolean }) => {
    const onSelectDate = (d?: DateTime) => {
      onChange && onChange(d?.setZone(timezone));
      if (d) {
        setPickerView('time');
      } else {
        setIsDateTimePickerOpen(false);
      }
    };

    const onSelectTime = (d?: DateTime) => {
      onChange && onChange(d);
      setIsDateTimePickerOpen(false);
      setPickerView('date');
    };

    return (
      <div className="date-picker">
        <div className="date-wrapper">
          {pickerView === 'date' && (
            <Calendar
              value={value}
              disabledDaysOfWeek={disabledDaysOfWeek}
              disabledIntervals={disabledIntervals}
              highlightedIntervals={highlightedIntervals}
              onChange={onSelectDate}
              showClearButton={showClearButton}
            />
          )}
          {pickerView === 'time' && (
            <div>
              <div className="time-picker-top">
                <i className="fa fa-arrow-left" onClick={() => setPickerView('date')} />
                <Typography type="text-sm"> {value?.toFormat('D')}</Typography>
                <i className="fa fa-arrow-left place-holder" />
              </div>
              <TimePicker
                value={value}
                onChange={onSelectTime}
                disabledIntervals={disabledIntervals}
                highlightedIntervals={highlightedIntervals}
                step={step}
                min={minTime}
                max={maxTime}
                restrictToTimes={restrictToTimes}
                timezone={timezone}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={divRef} className="date-picker-wrapper" {...props}>
      {customDisplay ? (
        <div
          className="cursor-pointer"
          onClick={() => setIsDateTimePickerOpen(true)}
          ref={usePortal ? setPortalWrapper : undefined}
        >
          {customDisplay}
        </div>
      ) : (
        <>
          <FieldLabel label={label} labelType={labelType} labelWeight={labelWeight} isRequired={isRequired} />

          <div
            ref={usePortal ? setPortalWrapper : undefined}
            className={classNames('input-wrapper-date flex justify-between', {
              focused: isDateTimePickerOpen,
              invalid: error,
            })}
            onClick={() => setIsDateTimePickerOpen(true)}
          >
            <div className="flex items-center">
              <Icon icon="Calendar" />
              <input
                className={classNames({ invalid: !!error })}
                hidden={true}
                name={name}
                value={value?.toISO() || ''}
                readOnly={true}
              />
              <Typography type="text-sm">
                <input
                  className={classNames({ valid: !!error })}
                  type="text"
                  value={inputDateStr || ''}
                  onChange={(e) => {
                    setInputDateStr(e.target.value);
                    setDidUpdateDateStr(true);
                  }}
                />
              </Typography>
            </div>

            <Button
              className={classNames('button button-tertiary', { invisible: !clearable || !value })}
              disabled={!clearable}
              onClick={(e) => {
                e.stopPropagation();
                onChange && onChange(undefined);
              }}
            >
              <Icon icon="X" size="small" title="Clear"></Icon>
            </Button>
          </div>
        </>
      )}

      <span className={classNames('error-message', { visible: error })}>
        {customErrorDisplay && error ? customErrorDisplay : error}
      </span>

      {isDateTimePickerOpen &&
        (usePortal ? (
          <Portal>
            <div ref={setPortalContainer} style={styles.popper} {...attributes.popper}>
              <div ref={portalContent}>
                <DateTimePickerDropdown showClearButton={clearable} />
              </div>
            </div>
          </Portal>
        ) : (
          <DateTimePickerDropdown />
        ))}
    </div>
  );
};
